import { ref } from "vue";
import { useSdk } from "@/composables/useSdk";
import { debounce } from "@/utils/debounce";
import type { UserProfile } from "@cc-consulting-nv/ccsdk";

/** Aligned with Parler QuickSearch-style dropdown (min chars before hitting API). */
export const SEARCH_PAGE_AUTOCOMPLETE_MIN_LENGTH = 2;

export const SEARCH_PAGE_AUTOCOMPLETE_MAX_USERS = 5;
export const SEARCH_PAGE_AUTOCOMPLETE_MAX_TAGS = 5;

/**
 * Strips a leading @ or # so user + hashtag APIs get the same term the user is typing.
 */
function autocompleteTerm(raw: string): string {
  const t = raw.trim();
  if (t.startsWith("@")) return t.slice(1).trim();
  if (t.startsWith("#")) return t.slice(1).trim();
  return t;
}

/**
 * Live users + tags for the /search header field. Users are listed first; tags second.
 * Uses `sdk.searchUsersAutocomplete` and `sdk.searchHashtags` (same as SearchView / useSearchInput).
 */
export function useSearchPageAutocomplete(options: {
  getQuery: () => string;
  debounceMs?: number;
}) {
  const { sdk } = useSdk();
  const userResults = ref<UserProfile[]>([]);
  const hashtagResults = ref<Array<{ name: string }>>([]);
  const isLoading = ref(false);

  const debounceMs = options.debounceMs ?? 200;

  /** Latest normalized term for this field; stale in-flight responses must not overwrite refs. */
  let latestAutocompleteTerm = "";

  async function fetchNow() {
    const raw = options.getQuery();
    const term = autocompleteTerm(raw);
    latestAutocompleteTerm = term;

    if (term.length < SEARCH_PAGE_AUTOCOMPLETE_MIN_LENGTH) {
      userResults.value = [];
      hashtagResults.value = [];
      isLoading.value = false;
      return;
    }

    isLoading.value = true;
    try {
      const [usersRes, tagsRes] = await Promise.all([
        sdk.searchUsersAutocomplete(term).catch((e) => {
          console.error("[useSearchPageAutocomplete] User search failed:", e);
          return { data: [] as UserProfile[] };
        }),
        sdk.searchHashtags(term).catch((e) => {
          console.error("[useSearchPageAutocomplete] Hashtag search failed:", e);
          return { data: { items: [] as Array<{ name: string }> } };
        }),
      ]);

      if (term !== latestAutocompleteTerm) return;

      const usersData = usersRes?.data;
      const users: UserProfile[] = Array.isArray(usersData) ? usersData : [];
      userResults.value = users.slice(0, SEARCH_PAGE_AUTOCOMPLETE_MAX_USERS);

      const items = tagsRes?.data?.items;
      const tags = Array.isArray(items) ? items : [];
      hashtagResults.value = tags.slice(0, SEARCH_PAGE_AUTOCOMPLETE_MAX_TAGS);
    } finally {
      if (term === latestAutocompleteTerm) {
        isLoading.value = false;
      }
    }
  }

  const debouncedFetch = debounce(fetchNow, debounceMs);

  return {
    userResults,
    hashtagResults,
    isLoading,
    debouncedFetch,
    fetchNow,
  };
}
